import { fmtNum } from "@/lib/api";
import SalesMarginPreview from "@/components/SalesMarginPreview";

const active = (s) => s.status === "Active" || s.status === "Approved";
const activeSpa = (s) => s.status === "Active" || s.status === "Partially Fulfilled";

export const MODULES = {
  suppliers: {
    endpoint: "suppliers", title: "Supplier Management", singular: "Supplier",
    subtitle: "Approved supplier registry with due diligence and risk rating.",
    createPerm: "supplier.manage", createLabel: "New Supplier",
    columns: [
      { key: "code", label: "Code", type: "mono" },
      { key: "name", label: "Supplier" },
      { key: "classification", label: "Class" },
      { key: "country", label: "Country" },
      { key: "license_no", label: "License", type: "mono" },
      { key: "risk_rating", label: "Risk", type: "risk" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "name", label: "Legal Name", required: true },
      { name: "classification", label: "Classification", type: "select", options: [
        { value: "Refiner", label: "Refiner" }, { value: "Trader", label: "Trader" }, { value: "Distributor", label: "Distributor" }] },
      { name: "country", label: "Country", placeholder: "Liberia" },
      { name: "email", label: "Email" }, { name: "phone", label: "Phone" },
      { name: "tax_id", label: "Tax ID" },
      { name: "bank_name", label: "Bank Name" }, { name: "bank_account", label: "Bank Account" },
      { name: "license_no", label: "Petroleum License No." },
      { name: "license_expiry", label: "License Expiry", type: "date" },
      { name: "risk_rating", label: "Risk Rating", type: "select", options: [
        { value: "Low", label: "Low" }, { value: "Medium", label: "Medium" }, { value: "High", label: "High" }, { value: "Critical", label: "Critical" }] },
    ],
    actions: (r) => {
      if (r.status === "Draft" || r.status === "Rejected") return [{ key: "submit", label: "Submit", perm: "supplier.manage", primary: true }];
      if (r.status === "Pending Approval") return [
        { key: "approve", label: "Approve", perm: "supplier.approve", primary: true },
        { key: "reject", label: "Reject", perm: "supplier.approve", variant: "destructive", inputs: [{ name: "reason", label: "Rejection Reason", type: "textarea", required: true }] }];
      return [];
    },
  },

  spas: {
    endpoint: "spas", title: "Sales & Purchase Agreements", singular: "SPA",
    subtitle: "Contract lifecycle: quantity, pricing, fulfillment tracking.",
    createPerm: "spa.manage", createLabel: "New SPA",
    columns: [
      { key: "spa_no", label: "SPA No.", type: "mono" },
      { key: "supplier_name", label: "Supplier" },
      { key: "product_name", label: "Product" },
      { key: "quantity", label: "Qty (L)", type: "num" },
      { key: "transferred_qty", label: "Transferred", type: "num" },
      { key: "unit_price", label: "Unit $", type: "money" },
      { key: "total_value", label: "Total", type: "money" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "supplier_id", label: "Supplier", type: "select", required: true,
        optionsSource: { endpoint: "suppliers", filter: active, label: (r) => `${r.name} (${r.code})` } },
      { name: "product_id", label: "Product", type: "select", required: true,
        optionsSource: { endpoint: "products", label: (r) => r.name } },
      { name: "quantity", label: "Quantity (Gallons)", type: "number", required: true },
      { name: "unit_price", label: "Unit Price (USD)", type: "number", required: true },
      { name: "payment_terms", label: "Payment Terms", placeholder: "Net 30" },
      { name: "delivery_terms", label: "Delivery Terms", placeholder: "CIF Monrovia" },
      { name: "start_date", label: "Start Date", type: "date" },
      { name: "end_date", label: "End Date", type: "date" },
    ],
    actions: (r) => {
      if (r.status === "Draft" || r.status === "Rejected") return [{ key: "submit", label: "Submit", perm: "spa.manage", primary: true }];
      if (r.status === "Pending Approval") return [
        { key: "approve", label: "Approve", perm: "spa.approve", primary: true },
        { key: "reject", label: "Reject", perm: "spa.approve", variant: "destructive", inputs: [{ name: "reason", label: "Rejection Reason", type: "textarea", required: true }] }];
      return [];
    },
  },

  "supplier-invoices": {
    endpoint: "supplier-invoices", title: "Supplier Invoices", singular: "Invoice",
    subtitle: "Three-way matching against SPA terms with exception flags.",
    createPerm: "invoice.manage", createLabel: "New Invoice",
    columns: [
      { key: "invoice_no", label: "Invoice No.", type: "mono" },
      { key: "supplier_ref", label: "Supplier Ref", type: "mono" },
      { key: "supplier_name", label: "Supplier" },
      { key: "quantity", label: "Qty (L)", type: "num" },
      { key: "total", label: "Total", type: "money" },
      { key: "match", label: "3-Way Match", get: (r) => (r.match_exceptions?.length ? `${r.match_exceptions.length} exception(s)` : "Matched") },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "spa_id", label: "SPA", type: "select", required: true,
        optionsSource: { endpoint: "spas", filter: activeSpa, label: (r) => `${r.spa_no} · ${r.supplier_name}` } },
      { name: "invoice_no", label: "Supplier Invoice Ref", required: true },
      { name: "quantity", label: "Quantity (Gallons)", type: "number", required: true },
      { name: "unit_price", label: "Unit Price (USD)", type: "number", required: true },
      { name: "taxes", label: "Taxes (USD)", type: "number" },
      { name: "fees", label: "Fees (USD)", type: "number" },
      { name: "invoice_date", label: "Invoice Date", type: "date" },
      { name: "due_date", label: "Due Date", type: "date" },
    ],
    actions: (r) => {
      if (r.status === "Draft" || r.status === "Under Review") return [{ key: "verify", label: "Verify", perm: "invoice.verify", primary: true }];
      if (r.status === "Verified") return [{ key: "approve", label: "Approve", perm: "payment.approve", primary: true }];
      return [];
    },
  },

  "supplier-payments": {
    endpoint: "supplier-payments", title: "Supplier Payments", singular: "Payment",
    subtitle: "Payment authorization, confirmation and reconciliation.",
    createPerm: "payment.manage", createLabel: "New Payment",
    columns: [
      { key: "payment_no", label: "Payment No.", type: "mono" },
      { key: "invoice_no", label: "Invoice", type: "mono" },
      { key: "supplier_name", label: "Supplier" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "method", label: "Method" },
      { key: "reference", label: "Bank Ref", type: "mono" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "invoice_id", label: "Approved Invoice", type: "select", required: true,
        optionsSource: { endpoint: "supplier-invoices", filter: (r) => r.status === "Approved", label: (r) => `${r.invoice_no} · ${r.supplier_name} · $${fmtNum(r.total, 2)}` } },
      { name: "bank", label: "Paying Bank" },
      { name: "method", label: "Method", type: "select", options: [
        { value: "Wire", label: "Wire" }, { value: "LC", label: "Letter of Credit" }, { value: "Transfer", label: "Bank Transfer" }] },
      { name: "reference", label: "Payment Reference" },
      { name: "payment_date", label: "Payment Date", type: "date" },
    ],
    actions: (r) => {
      if (r.status === "Draft") return [{ key: "approve", label: "Authorize", perm: "payment.approve", primary: true }];
      if (r.status === "Authorized") return [{ key: "confirm", label: "Confirm", perm: "payment.manage", primary: true }];
      return [];
    },
  },

  "lprc-transfers": {
    endpoint: "lprc-transfers", title: "LPRC Transfer Management", singular: "Transfer",
    subtitle: "Transfer requests, execution and inventory posting to tanks.",
    createPerm: "lprc.manage", createLabel: "New Transfer",
    columns: [
      { key: "transfer_no", label: "Transfer No.", type: "mono" },
      { key: "lprc_ref", label: "LPRC Ref", type: "mono" },
      { key: "supplier_name", label: "Supplier" },
      { key: "product_name", label: "Product" },
      { key: "tank_no", label: "Tank", type: "mono" },
      { key: "quantity", label: "Auth Qty", type: "num" },
      { key: "actual_quantity", label: "Actual", type: "num" },
      { key: "variance", label: "Variance", type: "num" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "spa_id", label: "SPA (paid)", type: "select", required: true,
        optionsSource: { endpoint: "spas", filter: activeSpa, label: (r) => `${r.spa_no} · ${r.supplier_name}` } },
      { name: "tank_id", label: "Destination Tank", type: "select", required: true,
        optionsSource: { endpoint: "tanks", label: (r) => `${r.tank_no} · ${r.product_name}` } },
      { name: "lprc_ref", label: "LPRC Reference" },
      { name: "quantity", label: "Authorized Quantity (L)", type: "number", required: true },
      { name: "transfer_date", label: "Transfer Date", type: "date" },
    ],
    actions: (r) => {
      if (r.status === "Requested") return [{ key: "approve", label: "Approve", perm: "lprc.approve", primary: true }];
      if (r.status === "Approved") return [{ key: "execute", label: "Execute", perm: "lprc.manage", primary: true,
        inputs: [{ name: "actual_quantity", label: "Actual Received Quantity (L)", type: "number", required: true }] }];
      return [];
    },
  },

  "inventory-adjustments": {
    endpoint: "inventory-adjustments", title: "Inventory Adjustments", singular: "Adjustment",
    subtitle: "Controlled adjustments require reason and independent approval.",
    createPerm: "inventory.adjust", createLabel: "New Adjustment",
    columns: [
      { key: "adjustment_no", label: "Adj No.", type: "mono" },
      { key: "tank_no", label: "Tank", type: "mono" },
      { key: "quantity", label: "Qty (±L)", type: "num" },
      { key: "reason", label: "Reason" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "tank_id", label: "Tank", type: "select", required: true,
        optionsSource: { endpoint: "tanks", label: (r) => `${r.tank_no} · ${r.product_name}` } },
      { name: "tank_no", label: "Tank No. (label)", placeholder: "TK-01" },
      { name: "quantity", label: "Quantity (+ or -)", type: "number", required: true },
      { name: "reason", label: "Reason", type: "textarea", required: true },
    ],
    actions: (r) => (r.status === "Pending Approval" ? [{ key: "approve", label: "Approve", perm: "inventory.adjust.approve", primary: true }] : []),
  },

  customers: {
    endpoint: "customers", title: "Customer Management", singular: "Customer",
    subtitle: "Off-taker registry with credit limits and risk rating.",
    createPerm: "customer.manage", createLabel: "New Customer",
    columns: [
      { key: "code", label: "Code", type: "mono" },
      { key: "name", label: "Customer" },
      { key: "type", label: "Type" },
      { key: "payment_terms", label: "Terms" },
      { key: "credit_limit", label: "Credit Limit", type: "money" },
      { key: "risk_rating", label: "Risk", type: "risk" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "name", label: "Customer Name", required: true },
      { name: "type", label: "Type", type: "select", options: [
        { value: "Off-taker", label: "Off-taker" }, { value: "Reseller", label: "Reseller" }, { value: "Industrial", label: "Industrial" }] },
      { name: "email", label: "Email" }, { name: "phone", label: "Phone" },
      { name: "tax_id", label: "Tax ID" },
      { name: "payment_terms", label: "Payment Terms", type: "select", options: [
        { value: "Prepayment", label: "Prepayment" }, { value: "Credit", label: "Credit" }] },
      { name: "credit_limit", label: "Credit Limit (USD)", type: "number" },
      { name: "risk_rating", label: "Risk Rating", type: "select", options: [
        { value: "Low", label: "Low" }, { value: "Medium", label: "Medium" }, { value: "High", label: "High" }] },
    ],
    actions: (r) => (r.status === "Pending Approval" ? [{ key: "approve", label: "Approve", perm: "customer.approve", primary: true }] : []),
  },

  "sales-orders": {
    endpoint: "sales-orders", title: "Sales Orders", singular: "Sales Order",
    subtitle: "Credit-checked orders with inventory reservation on approval.",
    createPerm: "sales.manage", createLabel: "New Sales Order", preview: SalesMarginPreview,
    columns: [
      { key: "so_no", label: "SO No.", type: "mono" },
      { key: "customer_name", label: "Customer" },
      { key: "product_name", label: "Product" },
      { key: "quantity", label: "Qty (L)", type: "num" },
      { key: "total_value", label: "Total", type: "money" },
      { key: "paid", label: "Paid", get: (r) => (r.paid ? "Yes" : "No") },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "customer_id", label: "Customer", type: "select", required: true,
        optionsSource: { endpoint: "customers", filter: (r) => r.status === "Active", label: (r) => `${r.name} (${r.code})` } },
      { name: "product_id", label: "Product", type: "select", required: true,
        optionsSource: { endpoint: "products", label: (r) => r.name } },
      { name: "tank_id", label: "Fulfil from Tank", type: "select", required: true,
        optionsSource: { endpoint: "tanks", label: (r) => `${r.tank_no} · ${r.product_name} · ${fmtNum(r.available)} gal avail` } },
      { name: "quantity", label: "Quantity (Gallons)", type: "number", required: true },
      { name: "unit_price", label: "Unit Price (USD)", type: "number", required: true },
    ],
    actions: (r) => {
      if (r.status === "Draft") return [{ key: "submit", label: "Submit", perm: "sales.manage", primary: true }];
      if (r.status === "Pending Approval") return [{ key: "approve", label: "Approve", perm: "sales.approve", primary: true }];
      return [];
    },
  },

  "customer-payments": {
    endpoint: "customer-payments", title: "Customer Payments", singular: "Receipt",
    subtitle: "Payment receipts with finance verification before release.",
    createPerm: "sales.manage", createLabel: "Record Payment",
    columns: [
      { key: "receipt_no", label: "Receipt No.", type: "mono" },
      { key: "so_no", label: "Sales Order", type: "mono" },
      { key: "customer_name", label: "Customer" },
      { key: "amount", label: "Amount", type: "money" },
      { key: "reference", label: "Bank Ref", type: "mono" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "sales_order_id", label: "Sales Order", type: "select", required: true,
        optionsSource: { endpoint: "sales-orders", filter: (r) => ["Approved", "DO Issued"].includes(r.status) && !r.paid, label: (r) => `${r.so_no} · ${r.customer_name} · $${fmtNum(r.total_value, 2)}` } },
      { name: "amount", label: "Amount (USD)", type: "number" },
      { name: "bank", label: "Bank" },
      { name: "reference", label: "Payment Reference" },
      { name: "payment_date", label: "Payment Date", type: "date" },
    ],
    actions: (r) => (r.status === "Received" ? [{ key: "verify", label: "Verify", perm: "customer_payment.verify", primary: true }] : []),
  },

  "delivery-orders": {
    endpoint: "delivery-orders", title: "Delivery Orders", singular: "Delivery Order",
    subtitle: "System-generated DOs issued only when all controls are satisfied.",
    createPerm: "do.issue", createLabel: "Issue DO",
    columns: [
      { key: "do_no", label: "DO No.", type: "mono" },
      { key: "customer_name", label: "Customer" },
      { key: "product_name", label: "Product" },
      { key: "quantity", label: "Qty (L)", type: "num" },
      { key: "vehicle", label: "Vehicle", type: "mono" },
      { key: "qr_code", label: "QR Code", type: "mono" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "sales_order_id", label: "Approved & Paid Sales Order", type: "select", required: true,
        optionsSource: { endpoint: "sales-orders", filter: (r) => r.status === "Approved", label: (r) => `${r.so_no} · ${r.customer_name} · ${fmtNum(r.quantity)} L` } },
      { name: "location", label: "Loading Location", placeholder: "Monrovia Depot" },
      { name: "vehicle", label: "Vehicle / Tanker" },
      { name: "driver", label: "Driver" },
      { name: "expiry_date", label: "DO Expiry", type: "date" },
    ],
    actions: () => [],
  },

  deliveries: {
    endpoint: "deliveries", title: "Deliveries & Proof of Delivery", singular: "Delivery",
    subtitle: "Actual lifting quantities, variance and recipient sign-off.",
    createPerm: "delivery.record", createLabel: "Record Delivery",
    columns: [
      { key: "delivery_no", label: "Delivery No.", type: "mono" },
      { key: "do_no", label: "DO", type: "mono" },
      { key: "customer_name", label: "Customer" },
      { key: "authorized_quantity", label: "Authorized", type: "num" },
      { key: "actual_quantity", label: "Actual", type: "num" },
      { key: "variance", label: "Variance", type: "num" },
      { key: "recipient", label: "Recipient" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "delivery_order_id", label: "Issued Delivery Order", type: "select", required: true,
        optionsSource: { endpoint: "delivery-orders", filter: (r) => r.status === "Issued", label: (r) => `${r.do_no} · ${r.customer_name} · ${fmtNum(r.quantity)} L` } },
      { name: "actual_quantity", label: "Actual Delivered Quantity (L)", type: "number", required: true },
      { name: "recipient", label: "Recipient Name (POD)", required: true },
    ],
    actions: () => [],
  },

  reconciliations: {
    endpoint: "reconciliations", title: "Reconciliation Engine", singular: "Reconciliation",
    subtitle: "Quantity and financial reconciliation across the transaction chain.",
    createPerm: "recon.manage", createLabel: "New Reconciliation",
    columns: [
      { key: "recon_no", label: "Recon No.", type: "mono" },
      { key: "spa_no", label: "SPA", type: "mono" },
      { key: "product_name", label: "Product" },
      { key: "purchased", label: "Purchased", type: "num" },
      { key: "transferred", label: "Transferred", type: "num" },
      { key: "delivered", label: "Delivered", type: "num" },
      { key: "quantity_variance", label: "Qty Variance", type: "num" },
      { key: "cost", label: "Cost", type: "money" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "spa_id", label: "SPA", type: "select", required: true,
        optionsSource: { endpoint: "spas", label: (r) => `${r.spa_no} · ${r.supplier_name}` } },
    ],
    actions: (r) => (r.status === "Open" ? [{ key: "close", label: "Close", perm: "recon.manage", primary: true }] : []),
  },

  vehicles: {
    endpoint: "vehicles", title: "Fleet & Tankers", singular: "Vehicle",
    subtitle: "Road tankers and vehicles available for dispatch.",
    createPerm: "logistics.manage", createLabel: "New Vehicle",
    columns: [
      { key: "reg_no", label: "Registration", type: "mono" },
      { key: "type", label: "Type" },
      { key: "capacity", label: "Capacity (L)", type: "num" },
      { key: "carrier", label: "Carrier" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "reg_no", label: "Registration No.", required: true, placeholder: "LR-TANK-0001" },
      { name: "type", label: "Type", type: "select", options: [
        { value: "Road Tanker", label: "Road Tanker" }, { value: "Rigid Truck", label: "Rigid Truck" }, { value: "Bowser", label: "Bowser" }] },
      { name: "capacity", label: "Capacity (Gallons)", type: "number" },
      { name: "carrier", label: "Carrier / Haulier" },
    ],
  },

  drivers: {
    endpoint: "drivers", title: "Drivers", singular: "Driver",
    subtitle: "Licensed drivers available for delivery dispatch.",
    createPerm: "logistics.manage", createLabel: "New Driver",
    columns: [
      { key: "name", label: "Driver" },
      { key: "license_no", label: "License", type: "mono" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "name", label: "Full Name", required: true },
      { name: "license_no", label: "Driving License No." },
      { name: "phone", label: "Phone" },
    ],
  },

  routes: {
    endpoint: "routes", title: "Delivery Routes", singular: "Route",
    subtitle: "Configured haulage routes for dispatch planning.",
    createPerm: "logistics.manage", createLabel: "New Route",
    columns: [
      { key: "name", label: "Route" },
      { key: "origin", label: "Origin" },
      { key: "destination", label: "Destination" },
      { key: "distance_km", label: "Distance (km)", type: "num" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "name", label: "Route Name", required: true, placeholder: "Monrovia → Gbarnga" },
      { name: "origin", label: "Origin" },
      { name: "destination", label: "Destination" },
      { name: "distance_km", label: "Distance (km)", type: "number" },
    ],
  },

  hsse: {
    endpoint: "hsse", title: "HSSE — Health, Safety, Security & Environment", singular: "Incident",
    subtitle: "Incident reporting, spills, injuries and safety inspections.",
    createPerm: "hsse.manage", createLabel: "Report Incident",
    columns: [
      { key: "incident_no", label: "Ref", type: "mono" },
      { key: "type", label: "Type" },
      { key: "severity", label: "Severity", type: "sev" },
      { key: "location", label: "Location" },
      { key: "description", label: "Description" },
      { key: "status", label: "Status", type: "status" },
    ],
    createFields: [
      { name: "type", label: "Incident Type", type: "select", required: true, options: [
        { value: "Spill", label: "Spill / Leak" }, { value: "Injury", label: "Injury" },
        { value: "Near Miss", label: "Near Miss" }, { value: "Fire", label: "Fire" },
        { value: "Environmental", label: "Environmental" }, { value: "Inspection", label: "Safety Inspection" }] },
      { name: "severity", label: "Severity", type: "select", options: [
        { value: "Low", label: "Low" }, { value: "Medium", label: "Medium" }, { value: "High", label: "High" }, { value: "Critical", label: "Critical" }] },
      { name: "location", label: "Location" },
      { name: "incident_date", label: "Incident Date", type: "date" },
      { name: "description", label: "Description", type: "textarea", required: true },
    ],
    actions: (r) => {
      if (r.status === "Reported") return [
        { key: "investigate", label: "Investigate", perm: "hsse.manage", primary: true, toast: "Investigation started" }];
      if (r.status === "Investigating") return [
        { key: "close", label: "Close", perm: "hsse.manage", primary: true,
          inputs: [{ name: "root_cause", label: "Root Cause", type: "textarea", required: true },
                   { name: "corrective_action", label: "Corrective Action", type: "textarea", required: true }] }];
      return [];
    },
  },

  products: {
    endpoint: "products", title: "Petroleum Product Master", singular: "Product",
    subtitle: "Configurable product catalog with measurement basis.",
    createPerm: "product.manage", createLabel: "New Product",
    columns: [
      { key: "code", label: "Code", type: "mono" },
      { key: "name", label: "Product" },
      { key: "category", label: "Category" },
      { key: "unit", label: "Unit" },
      { key: "density", label: "Density", type: "num", dp: 2 },
    ],
    createFields: [
      { name: "code", label: "Product Code", required: true, placeholder: "PMS" },
      { name: "name", label: "Product Name", required: true },
      { name: "category", label: "Category", type: "select", options: [
        { value: "Refined", label: "Refined" }, { value: "Crude", label: "Crude" }, { value: "LPG", label: "LPG" }] },
      { name: "unit", label: "Base Unit", type: "select", options: [
        { value: "Gallons", label: "Gallons" }, { value: "Kilolitres", label: "Kilolitres" }, { value: "Metric Tonnes", label: "Metric Tonnes" }, { value: "Barrels", label: "Barrels" }] },
      { name: "density", label: "Density (kg/gal)", type: "number" },
      { name: "spec", label: "Specification" },
    ],
  },
};
